// src/hooks/notifications/useSharedEventBridgeNotifications.js
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { ThreadNotificationHandler } from "../../utils/notification/threadNotificationHandler";

const WEBSOCKET_URL =
  process.env.REACT_APP_EVENTBRIDGE_WS_URL ||
  "wss://mp1mbrtdse.execute-api.us-west-2.amazonaws.com/dev";

// Singleton state - shared across all hook instances
let sharedState = {
  notifications: [],
  isConnected: false,
  connectionError: null,
  wsRef: null,
  reconnectTimeoutRef: null,
  subscribers: new Set(),
  isInitialized: false,
};

// Notify all subscribers when state changes
const notifySubscribers = () => {
  sharedState.subscribers.forEach((callback) => {
    try {
      callback({
        notifications: [...sharedState.notifications],
        isConnected: sharedState.isConnected,
        connectionError: sharedState.connectionError,
      });
    } catch (error) {
      // Ignore notification errors to prevent crashes
    }
  });
};

// Shared connection management
const connectWebSocket = async (getAccessTokenSilently) => {
  if (sharedState.wsRef && sharedState.wsRef.readyState === WebSocket.OPEN) {
    return;
  }

  try {
    const token = await getAccessTokenSilently();
    if (!token) {
      throw new Error("No authentication token found");
    }

    const ws = new WebSocket(`${WEBSOCKET_URL}?token=${token}`);
    sharedState.wsRef = ws;

    ws.onopen = () => {
      sharedState.isConnected = true;
      sharedState.connectionError = null;
      notifySubscribers();

      // Send a test message to verify the connection works
      setTimeout(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping", timestamp: Date.now() }));
        }
      }, 2000);
    };

    ws.onmessage = (event) => {
      try {
        const notification = JSON.parse(event.data);

        // Validate message format
        if (notification.type === "notification" && notification.data) {
          // Add notification to shared state
          sharedState.notifications = [
            notification,
            ...sharedState.notifications,
          ];

          // Limit to 50 most recent notifications
          if (sharedState.notifications.length > 50) {
            sharedState.notifications = sharedState.notifications.slice(0, 50);
          }

          // Show browser notification if permission granted
          if (Notification.permission === "granted") {
            showBrowserNotification(notification);
          }

          // Notify all subscribers
          notifySubscribers();
        } else {
          // Ignore unexpected message formats
        }
      } catch (error) {
        console.error("❌ EventBridge: Error parsing notification:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("❌ EventBridge: WebSocket error:", error);
      sharedState.connectionError = "Connection error occurred";
      notifySubscribers();
    };

    ws.onclose = (event) => {
      sharedState.isConnected = false;

      // Auto-reconnect after 3 seconds if not intentional close
      if (event.code !== 1000 && sharedState.subscribers.size > 0) {
        sharedState.reconnectTimeoutRef = setTimeout(() => {
          connectWebSocket(getAccessTokenSilently);
        }, 3000);
      }

      notifySubscribers();
    };
  } catch (error) {
    console.error("EventBridge: Failed to connect:", error);
    sharedState.connectionError = error.message;
    notifySubscribers();
  }
};

const disconnectWebSocket = () => {
  if (sharedState.reconnectTimeoutRef) {
    clearTimeout(sharedState.reconnectTimeoutRef);
    sharedState.reconnectTimeoutRef = null;
  }
  if (sharedState.wsRef) {
    sharedState.wsRef.close(1000); // Normal closure
    sharedState.wsRef = null;
  }
  sharedState.isConnected = false;
  sharedState.connectionError = null;
};

// Browser notification helper
const showBrowserNotification = (notification) => {
  const { data } = notification;

  // Handle thread notifications with enhanced display
  if (ThreadNotificationHandler.isThreadNotification(notification)) {
    const threadText =
      ThreadNotificationHandler.getThreadNotificationText(notification);

    new Notification(threadText.title, {
      body: threadText.message,
      icon: data.metadata?.actorPicture || "/favicon.ico",
      tag: data._id,
      data: {
        threadId: data.threadId,
        messageId: data.messageId,
        replyId: data.replyId,
        type: data.type,
      },
    });
  } else if (data.type === "fighter_cancelled") {
    // Handle fighter cancellation notifications
    const { fighterName, eventName } = data.metadata || {};
    new Notification("Fight Cancelled", {
      body: `${fighterName}'s fight has been cancelled for ${eventName}. They will score 0 points.`,
      icon: "/favicon.ico",
      tag: data._id,
      data: {
        type: data.type,
        eventId: data.metadata?.eventId,
      },
    });
  } else {
    // Handle regular notifications
    const title = getNotificationTitle(data.type);
    const body = `${data.metadata?.actorName} ${getNotificationMessage(
      data.type,
    )}`;

    new Notification(title, {
      body,
      icon: data.metadata?.actorPicture || "/favicon.ico",
      tag: data._id,
    });
  }
};

const getNotificationTitle = (type) => {
  const titles = {
    follow: "New Follower",
    unfollow: "Unfollowed",
    fighter_cancelled: "Fight Cancelled",
    "message.liked": "Message Liked",
    "message.replied": "New Reply",
  };
  return titles[type] || "New Notification";
};

const getNotificationMessage = (type) => {
  const messages = {
    follow: "started following you",
    unfollow: "unfollowed you",
    fighter_cancelled: "fight cancelled - will score 0 points",
    "message.liked": "liked your message",
    "message.replied": "replied to your message",
  };
  return messages[type] || "sent you a notification";
};

/**
 * Shared EventBridge notifications hook
 * Creates a single WebSocket connection shared across all components
 */
export const useSharedEventBridgeNotifications = () => {
  const [localState, setLocalState] = useState({
    notifications: sharedState.notifications,
    isConnected: sharedState.isConnected,
    connectionError: sharedState.connectionError,
  });

  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const subscriberRef = useRef(null);

  // Create subscriber callback
  const updateLocalState = useCallback((newState) => {
    setLocalState(newState);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Add this component as a subscriber
    subscriberRef.current = updateLocalState;
    sharedState.subscribers.add(subscriberRef.current);

    // Initialize connection if this is the first subscriber
    if (!sharedState.isInitialized) {
      sharedState.isInitialized = true;
      connectWebSocket(getAccessTokenSilently);
    }

    // Cleanup on unmount
    return () => {
      if (subscriberRef.current) {
        sharedState.subscribers.delete(subscriberRef.current);

        // Disconnect if no more subscribers
        if (sharedState.subscribers.size === 0) {
          disconnectWebSocket();
          sharedState.isInitialized = false;
        }
      }
    };
  }, [isAuthenticated, getAccessTokenSilently, updateLocalState]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    sharedState.notifications = sharedState.notifications.map((notif) =>
      notif.data._id === notificationId
        ? { ...notif, data: { ...notif.data, read: true } }
        : notif,
    );
    notifySubscribers();
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    sharedState.notifications = [];
    notifySubscribers();
  }, []);

  return {
    notifications: localState.notifications,
    isConnected: localState.isConnected,
    connectionError: localState.connectionError,
    markAsRead,
    clearNotifications,
  };
};

export default useSharedEventBridgeNotifications;
